import {create} from 'zustand'
import {Song} from "../types";
import toast from "react-hot-toast";
import LocalStorage, {SONG_HISTORY_KEY, SONG_QUEUE_KEY, CURR_SONG_KEY} from "../../../../utils/local-storage";
import {areSameSongs} from "../../../../utils/utils";
import { DiscordBotApi } from '../../../../utils/discordBotApi';

export type LoopMode = "none" | "queue" | "song";

export interface SavedLocalState {
    currentSong?: Song;
    songQueue: Song[];
    songHistory: Song[];
    loopMode: LoopMode;
    randomQueue: boolean;
    originalQueueOrder: number[];
}

interface SongState {
    currentSong?: Song;
    loadedSongs: Song[];
    loadingSongs: boolean;
    songQueue: Song[];
    songHistory: Song[];
    loopMode: LoopMode;
    randomQueue: boolean;
    originalQueueOrder: number[]; //ids list
    isBotMode: boolean;
    botGuildId: string | null;
    botVoiceChannelId: string | null;
    botPlaying: boolean;
    botPlaybackSeconds: number;
    botDuration: number;
    savedLocalState: SavedLocalState | null;
}

interface SongStateStore {
    state: SongState;
    setCurrentSong: (song?: Song) => void;
    addSongToQueue: (song: Song) => void;
    addSongToPlayNext: (song: Song) => void;
    setQueue: (songs: Song[]) => void;
    playNextSong: () => void;
    setSongs: (songs: Song[]) => void;
    emptyQueue: () => void;
    emptyHistory: () => void;
    setLoopMode: (mode: LoopMode) => void;
    setRandomQueue: (random: boolean) => void;
    setOriginalQueueOrder: (order: number[]) => void;
    setBotMode: (active: boolean, guildId?: string | null, voiceChannelId?: string | null) => void;
    syncWithBotState: (
        currentSong: Song | null,
        songQueue: Song[],
        history: Song[],
        loopMode: LoopMode,
        playing: boolean,
        playbackSeconds: number,
        duration: number
    ) => void;
    updateSongInStore: (updatedSong: Song) => void;
    removeSongFromStore: (songId: string | number) => void;
}

const addToHistory = (song: Song, currentHistory: Song[]) => {
    if (song.id === -1) {
        return currentHistory;
    }
    const newHistory = [song, ...currentHistory];
    //if (newHistory.length > 10) {
    //    newHistory.pop();
    //}
    LocalStorage.setItem(SONG_HISTORY_KEY, newHistory);
    return newHistory;
}

const handleSetCurrentSong = (state: SongStateStore, song?: Song) => {
    // If the song is in the queue, remove it
    const newQueue = state.state.songQueue.filter((s) => s.id !== song?.id);
    let newHistory = state.state.songHistory;
    if (state.state.currentSong?.id && state.state.currentSong.id !== song?.id) {
        newHistory = addToHistory(state.state.currentSong, newHistory);
    }

    if (song && areSameSongs(song, state.state.currentSong) && song.song_url === state.state.currentSong?.song_url && song.canvas_url === state.state.currentSong?.canvas_url && song.name === state.state.currentSong?.name && song.poster_url === state.state.currentSong?.poster_url) {
        return state;
    }

    return {...state, state: {...state.state, currentSong: song, songHistory: newHistory, songQueue: newQueue}}
}

const useSongStore = create<SongStateStore>()((set) => ({
    state: {
        currentSong: undefined,
        loadedSongs: [],
        loadingSongs: false,
        songQueue: [],
        songHistory: LocalStorage.getItem(SONG_HISTORY_KEY) || [],
        loopMode: LocalStorage.getItem<LoopMode>('loopMode') || "none",
        randomQueue: LocalStorage.getItem<boolean>('randomQueue') || false,
        originalQueueOrder: LocalStorage.getItem<number[]>('originalQueueOrder') || [],
        isBotMode: LocalStorage.getItem<boolean>('isBotMode') || false,
        botGuildId: LocalStorage.getItem<string | null>('botGuildId') || null,
        botVoiceChannelId: LocalStorage.getItem<string | null>('botVoiceChannelId') || null,
        botPlaying: false,
        botPlaybackSeconds: 0,
        botDuration: 0,
        savedLocalState: null
    },
    setCurrentSong: (song?: Song) => set((state) => {
        if (state.state.isBotMode && state.state.botGuildId && song) {
            (async () => {
                try {
                    await DiscordBotApi.stop(state.state.botGuildId!);
                } catch (e) {
                    console.warn("Could not stop bot before play:", e);
                }
                try {
                    await DiscordBotApi.play(
                        state.state.botGuildId!,
                        `https://spotip17an.es/share?song=${song.id}`,
                        state.state.botVoiceChannelId || undefined
                    );
                } catch (e) {
                    console.error("Error playing in Bot Mode:", e);
                }
            })();
        }
        return handleSetCurrentSong(state, song);
    }),
    setSongs: (songs: Song[]) => set((state) => {
        return {...state, state: {...state.state, loadedSongs: songs, loadingSongs: false}};
    }),
    addSongToQueue: (song: Song) => set((state) => {
        if (state.state.isBotMode && state.state.botGuildId) {
            DiscordBotApi.play(
                state.state.botGuildId,
                `https://spotip17an.es/share?song=${song.id}`,
                state.state.botVoiceChannelId || undefined
            ).then(() => toast.success('Canción añadida a la cola del bot'))
              .catch(e => console.error("Error enqueuing in Bot Mode:", e));
        } else {
            toast.success('Canción añadida a la cola');
        }

        if (state.state.songQueue.some((s) => s.id === song.id)) {
            toast.error('La canción ya está en la cola');
            return state;
        }
        if (!state.state.currentSong?.id) {
            return handleSetCurrentSong(state, song);
        }

        const newQueue = [...state.state.songQueue, song];
        LocalStorage.setItem(SONG_QUEUE_KEY, newQueue);
        return {...state, state: {...state.state, songQueue: newQueue}}
    }),
    addSongToPlayNext: (song: Song) => set((state) => {
        if (state.state.isBotMode && state.state.botGuildId) {
            DiscordBotApi.play(
                state.state.botGuildId,
                `https://spotip17an.es/share?song=${song.id}`,
                state.state.botVoiceChannelId || undefined
            ).then(() => toast.success('La canción se reproducirá a continuación en el bot'))
              .catch(e => console.error("Error en playNext in Bot Mode:", e));
        } else {
            toast.success('La canción se reproducirá a continuación');
        }

        if (!state.state.currentSong?.id) {
            return handleSetCurrentSong(state, song);
        }

        const newQueue = [song, ...state.state.songQueue.filter((s) => s.id !== song.id)];
        LocalStorage.setItem(SONG_QUEUE_KEY, newQueue);
        return {...state, state: {...state.state, songQueue: newQueue}}
    }),
    playNextSong: () => {
        set((state) => {
            if (state.state.songQueue.length > 0) {
                const newQueue = state.state.songQueue.slice(1);
                let newHistory = state.state.songHistory;
                if (state.state.currentSong) {
                    newHistory = addToHistory(state.state.currentSong, state.state.songHistory);
                }
                LocalStorage.setItem(SONG_QUEUE_KEY, newQueue);


                const nextSong = state.state.songQueue[0];
                return {
                    ...state,
                    state: {
                        ...state.state,
                        currentSong: nextSong,
                        songQueue: newQueue,
                        songHistory: newHistory
                    }
                };
            } else {
                return state;
            }
        });
    },
    setQueue: (songs: Song[]) => set((state) => ({...state, state: {...state.state, songQueue: songs}})),
    emptyQueue: () => set((state) => {
        LocalStorage.setItem(SONG_QUEUE_KEY, []);
        return {...state, state: {...state.state, songQueue: []}};
    }),
    emptyHistory: () => set((state) => {
        LocalStorage.setItem(SONG_HISTORY_KEY, []);
        return {...state, state: {...state.state, songHistory: []}};
    }),
    setLoopMode: (mode: LoopMode) => {
        LocalStorage.setItem('loopMode', mode);
        set((state) => ({...state, state: {...state.state, loopMode: mode}}));
    },
    setRandomQueue: (random: boolean) => {
        LocalStorage.setItem('randomQueue', random);
        set((state) => ({...state, state: {...state.state, randomQueue: random}}));
    },
    setOriginalQueueOrder: (order: number[]) => {
        LocalStorage.setItem('originalQueueOrder', order);
        set((state) => ({...state, state: {...state.state, originalQueueOrder: order}}));
    },
    setBotMode: (active: boolean, guildId?: string | null, voiceChannelId?: string | null) => {
        LocalStorage.setItem('isBotMode', active);
        LocalStorage.setItem('botGuildId', guildId || null);
        LocalStorage.setItem('botVoiceChannelId', voiceChannelId || null);
        set((state) => {
            let savedLocalState = state.state.savedLocalState;
            if (active) {
                savedLocalState = {
                    currentSong: state.state.currentSong,
                    songQueue: state.state.songQueue,
                    songHistory: state.state.songHistory,
                    loopMode: state.state.loopMode,
                    randomQueue: state.state.randomQueue,
                    originalQueueOrder: state.state.originalQueueOrder
                };
            }

            const newState = {
                ...state.state,
                isBotMode: active,
                botGuildId: guildId || null,
                botVoiceChannelId: voiceChannelId || null,
                botPlaying: active ? state.state.botPlaying : false,
                botPlaybackSeconds: active ? state.state.botPlaybackSeconds : 0,
                botDuration: active ? state.state.botDuration : 0,
                savedLocalState
            };

            if (!active && savedLocalState) {
                newState.currentSong = savedLocalState.currentSong;
                newState.songQueue = savedLocalState.songQueue;
                newState.songHistory = savedLocalState.songHistory;
                newState.loopMode = savedLocalState.loopMode;
                newState.randomQueue = savedLocalState.randomQueue;
                newState.originalQueueOrder = savedLocalState.originalQueueOrder;
                newState.savedLocalState = null;

                LocalStorage.setItem(CURR_SONG_KEY, savedLocalState.currentSong || null);
                LocalStorage.setItem(SONG_QUEUE_KEY, savedLocalState.songQueue);
                LocalStorage.setItem(SONG_HISTORY_KEY, savedLocalState.songHistory);
                LocalStorage.setItem('loopMode', savedLocalState.loopMode);
                LocalStorage.setItem('randomQueue', savedLocalState.randomQueue);
                LocalStorage.setItem('originalQueueOrder', savedLocalState.originalQueueOrder);
            }

            return { ...state, state: newState };
        });
    },
    syncWithBotState: (currentSong, songQueue, history, loopMode, playing, playbackSeconds, duration) => set((state) => ({
        ...state,
        state: {
            ...state.state,
            currentSong: currentSong || undefined,
            songQueue,
            songHistory: history,
            loopMode,
            botPlaying: playing,
            botPlaybackSeconds: playbackSeconds,
            botDuration: duration
        }
    })),
    updateSongInStore: (updatedSong: Song) => set((state) => {
        let currentSong = state.state.currentSong;
        if (currentSong && (String(currentSong.id) === String(updatedSong.id) || areSameSongs(currentSong, updatedSong))) {
            currentSong = { ...currentSong, ...updatedSong };
            LocalStorage.setItem(CURR_SONG_KEY, currentSong);
        }

        const songQueue = state.state.songQueue.map(s => (String(s.id) === String(updatedSong.id) ? { ...s, ...updatedSong } : s));
        const songHistory = state.state.songHistory.map(s => (String(s.id) === String(updatedSong.id) ? { ...s, ...updatedSong } : s));
        const loadedSongs = state.state.loadedSongs.map(s => (String(s.id) === String(updatedSong.id) ? { ...s, ...updatedSong } : s));

        LocalStorage.setItem(SONG_QUEUE_KEY, songQueue);
        LocalStorage.setItem(SONG_HISTORY_KEY, songHistory);

        return {
            ...state,
            state: {
                ...state.state,
                currentSong,
                songQueue,
                songHistory,
                loadedSongs
            }
        };
    }),
    removeSongFromStore: (songId: string | number) => set((state) => {
        const idStr = String(songId);
        let currentSong = state.state.currentSong;
        let songQueue = state.state.songQueue.filter(s => String(s.id) !== idStr);
        let songHistory = state.state.songHistory.filter(s => String(s.id) !== idStr);
        let loadedSongs = state.state.loadedSongs.filter(s => String(s.id) !== idStr);

        LocalStorage.setItem(SONG_QUEUE_KEY, songQueue);
        LocalStorage.setItem(SONG_HISTORY_KEY, songHistory);

        if (currentSong && String(currentSong.id) === idStr) {
            if (songQueue.length > 0) {
                currentSong = songQueue[0];
                songQueue = songQueue.slice(1);
                LocalStorage.setItem(SONG_QUEUE_KEY, songQueue);
                LocalStorage.setItem(CURR_SONG_KEY, currentSong);
            } else {
                currentSong = undefined;
                LocalStorage.setItem(CURR_SONG_KEY, null);
            }
        }

        return {
            ...state,
            state: {
                ...state.state,
                currentSong,
                songQueue,
                songHistory,
                loadedSongs
            }
        };
    })
}))

export default useSongStore;
